import {useQueryClient} from "@tanstack/react-query";
import {useCommonItems} from "../../../../../../contexts/CommonItensProvider";
import {useAppContext} from "../../../../../../contexts/AppContext";
import {useModuleContext} from "../../../../../../contexts/ModuleProvider";
import {useEffect, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {carregaEstoques, carregaPontosEntrega, manipulaEquipamento} from "../../configs/functions";
import {fillInputErrors, getFormErrorMessage} from "../../../../../../configs/functions";
import {getDefaultValueSelectField} from "../../../../../../utils";
import {
    Box,
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle, Divider,
    FormControl,
    Grid, InputAdornment,
    MenuItem,
    TextField, Typography
} from "@mui/material";
import {TooltipSelectDisabled} from "../../../../../common/TooltipSelectDisabled";
import {grey} from "@mui/material/colors";
import LanIcon from "@mui/icons-material/Lan";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import FourGMobiledataIcon from "@mui/icons-material/FourGMobiledata";
import {CommonDialogActionsForm} from "../../../../../common/CommonDialogActionsForm";


export function DeskForm({ open, setOpen, equipamento, empresas, queryKey, tipo, type }) {
    const queryClient = useQueryClient();
    const { exibirDialog, handleCloseDialog, exibirAlerta } = useCommonItems();
    const { dadosUsuario } = useAppContext();
    const { verifyIntegrationMode } = useModuleContext();

    const isEditAction = !!equipamento;

    const [estoques, setEstoques] = useState([]);
    const [pontosEntrega, setPontosEntrega] = useState([]);
    const [selectedEmpresaId, setSelectedEmpresaId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasStockModule, setHasStockModule] = useState(false);

    const equipmentContractTypes = [{ value: 'locacao', text: 'Locação' }, { value: 'venda', text: 'Venda' }];

    const {
        register,
        handleSubmit,
        setValue,
        setError,
        clearErrors,
        reset,
        control,
        formState: { dirtyFields, errors },
    } = useForm();

    const loadPontosEntrega = async (empresaId) => {
        try {
            const filterOnlyActives = {
                status: 1,
                id_empresa: empresaId
            };
            const response = await carregaPontosEntrega(filterOnlyActives);
            setPontosEntrega(response.data.data);
        } catch (error) {
            exibirAlerta('Erro ao carregar os Pontos de Entrega', '', 'error');
        }
    }

    const loadEstoque = async (empresaId) => {
        try {
            const filterOnlyActives = {
                status: 1,
                id_empresas: [empresaId]
            };
            const response = await carregaEstoques(filterOnlyActives);
            const allStocks = response.data.data;

            const empresaHasActionsAllowed = await verifyIntegrationMode('estoque', selectedEmpresaId);

            // remover os que tenham classificacao como 'vending_machine'
            let filteredStocks;
            if (empresaHasActionsAllowed) {
                filteredStocks = allStocks.filter(stock => {
                    const classificacao = stock.classificacao?.[0] || stock.classificacao;
                    return classificacao !== 'vending_machine';
                });
            } else {
                filteredStocks = allStocks.filter(stock => {
                    const classificacao = stock.classificacao|| stock.classificacao;
                    return classificacao === 'principal';
                });
            }
            setEstoques(filteredStocks);
            setHasStockModule(true);
        } catch (error) {
            const messageError = error.response ? error.response.data.error : 'Erro ao carregar os Estoque';
            setHasStockModule(false);
            // se o erro for diferente de 'não tem acesso a esse módulo', exibe o alerta
            if (!messageError.includes('não tem acesso a esse módulo')) {
                exibirAlerta('Erro ao carregar os Estoque', '', 'error');
            }
        }
    }

    const onSubmit = async (data) => {
        let formDataToSend = {};
        setIsLoading(true);
        exibirDialog('Salvando. Aguarde...');

        if (isEditAction) {
            const dirtyFieldKeys = Object.keys(dirtyFields);

            const dirtyData = dirtyFieldKeys.reduce((acc, key) => {
                if (data.hasOwnProperty(key)) {
                    acc[key] = data[key];
                }
                return acc;
            }, {});

            if (Object.keys(dirtyData).length === 0) {
                exibirAlerta('Atenção', 'Nenhum dado foi alterado.', 'warning');
                handleCloseDialog();
                setIsLoading(false);
                return;
            }

            formDataToSend = dirtyData;
            formDataToSend.id = equipamento.id;
        } else {
            delete data.id;

            if (!data.tag_identificacao) {
                delete data.tag_identificacao;
            }

            formDataToSend = data;
        }

        // if !hasStockModule, remove id_estoque from formDataToSend
        if (!hasStockModule) {
            delete formDataToSend.id_estoque;
        }

        try {
            const res = await manipulaEquipamento(formDataToSend, isEditAction);
            exibirAlerta('Sucesso', res.data.message, 'success');
            setOpen(false);

            // quando backend passar a retornar objeto criado/editado, fazer as operacoes com o cache, ao inves de fazer o refetch
            // assim como foi feito no DocumentTypeForm.jsx

            // enquanto nao tem o obj criado/editado, invalidar a query para atualizar a lista
            queryClient.invalidateQueries({ queryKey });
        } catch (error) {
            let errorMessage = "Erro ao cadastrar Balcão.";
            if (error.response) {
                errorMessage = error.response.data.error
                fillInputErrors(error, setError);
            }
            exibirAlerta('Erro', errorMessage, 'error');
        } finally {
            setIsLoading(false);
            handleCloseDialog();
        }
    };

    useEffect(() => {
        // preenche o formulário ao abrir p/ edicao
        if (open) {
            const defaultValueIdEmpresa = getDefaultValueSelectField(equipamento, equipamento?.empresa?.id, empresas);

            const defaultValues = {
                id: equipamento ? equipamento.id : '',
                nome: equipamento ? equipamento.nome : '',
                tag_identificacao: equipamento ? equipamento.tag_identificacao : '',
                status: equipamento ? equipamento.status : 1,
                tipo: equipamento ? equipamento.tipo : type,
                tipo_contrato: equipamento ? equipamento?.tipo_contrato : 'locacao',
                descricao: equipamento ? equipamento.descricao : '',
                id_empresa: defaultValueIdEmpresa,
                id_ponto_entrega: equipamento?.pontoEntrega?.id ?? '',
                id_estoque: equipamento?.estoque?.id ?? '',
                ip_ethernet: equipamento?.ipEthernet ?? '',
                mac_ethernet: equipamento?.macEthernet ?? '',
                ip_wifi: equipamento?.ipWifi ?? '',
                mac_wifi: equipamento?.macWifi ?? '',
                ip_4g: equipamento?.ip4g ?? '',
                mac_4g: equipamento?.mac4g ?? '',
            };

            if (defaultValueIdEmpresa) {
                setSelectedEmpresaId(defaultValueIdEmpresa);
            }

            reset(defaultValues);
        } else {
            reset();
            setSelectedEmpresaId(null);
            setHasStockModule(false);
        }
    }, [empresas, equipamento, open, reset]);

    useEffect(() => {
        const defaultValueIdPontoEntrega = getDefaultValueSelectField(equipamento, equipamento?.pontoEntrega?.id, pontosEntrega);
        const defaultValueIdEstoque = getDefaultValueSelectField(equipamento, equipamento?.estoque?.id, estoques);

        setValue('id_ponto_entrega', defaultValueIdPontoEntrega, { shouldDirty: true });
        setValue('id_estoque', defaultValueIdEstoque, { shouldDirty: true });

        if (defaultValueIdPontoEntrega) {
            clearErrors('id_ponto_entrega');
        }

        if (defaultValueIdEstoque) {
            clearErrors('id_estoque');
        }
    }, [clearErrors, equipamento, estoques, pontosEntrega, setValue]);

    useEffect(() => {
        if (open && selectedEmpresaId) {
            loadPontosEntrega(selectedEmpresaId);
            loadEstoque(selectedEmpresaId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEmpresaId, open]);

    return (
        <Dialog open={open} fullWidth maxWidth={"lg"}>
            <form onSubmit={handleSubmit(onSubmit)}>
                {isEditAction
                    ? <DialogTitle>Editando {tipo} <strong>{equipamento.nome}</strong></DialogTitle>
                    : <DialogTitle>Cadastrar novo {tipo}</DialogTitle>
                }
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        {isEditAction ? 'Preencha corretamente os dados.' : 'Preencha corretamente os dados abaixo para cadastrar um novo ' + tipo + '.'}
                    </DialogContentText>

                    <Box sx={{ p: 2, backgroundColor: "#fafafa" }} >
                        <FormControl variant="standard" fullWidth >
                            <Grid
                                container
                                direction="row"
                                justifyContent="flex-start"
                                alignItems="flex-start"
                                spacing={2}
                            >
                                <Grid item xs={12} md={8}>
                                    <TextField
                                        InputLabelProps={{ shrink: true }}
                                        margin='dense'
                                        label='Nome'
                                        placeholder='Digite o nome do Balcão'
                                        name='nome'
                                        fullWidth
                                        {...register("nome", { required: true })}
                                        error={!!errors.nome}
                                        helperText={getFormErrorMessage(errors, 'nome')}
                                        variant="outlined"
                                        disabled={!dadosUsuario.isSempher}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        InputLabelProps={{ shrink: true }}
                                        margin='dense'
                                        label='Código de Identificação'
                                        placeholder='Digite o código de identificação'
                                        name='tag_identificacao'
                                        fullWidth
                                        {...register("tag_identificacao")}
                                        error={!!errors.tag_identificacao}
                                        helperText={!!errors.tag_identificacao ? getFormErrorMessage(errors, 'tag_identificacao') : 'O código será criado automaticamente se não preenchido.'}
                                        variant="outlined"
                                        disabled={!dadosUsuario.isSempher}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        InputLabelProps={{ shrink: true }}
                                        margin='dense'
                                        label='Descrição'
                                        placeholder='Digite a Descrição'
                                        name='descricao'
                                        fullWidth
                                        {...register("descricao")}
                                        variant="outlined"
                                        multiline
                                        rows={3}
                                        error={!!errors.descricao}
                                        helperText={getFormErrorMessage(errors, 'descricao')}
                                        disabled={!dadosUsuario.isSempher}
                                    />
                                </Grid>

                                {empresas &&
                                    <Grid container item xs={12} spacing={2}>
                                        <Grid item xs={12} md={3}>
                                            <TooltipSelectDisabled
                                                isDisabled={!(!!equipamento)}
                                                text="Não é possível alterar a Empresa de um Balcão já cadastrado"
                                            >
                                                <Controller
                                                    name='id_empresa'
                                                    control={control}
                                                    rules={{ required: true }}
                                                    defaultValue={equipamento ? equipamento.empresa.id : ""}
                                                    render={({ field: { ref, onChange, ...field } }) => (
                                                        <TextField
                                                            {...field}
                                                            select
                                                            fullWidth
                                                            sx={{ mt: 1 }}
                                                            label="Empresa"
                                                            disabled={!dadosUsuario.isSempher || !!equipamento}
                                                            defaultValue={equipamento ? equipamento.empresa.id : ""}
                                                            onChange={(e) => {
                                                                const selectedValue = e.target.value;
                                                                setSelectedEmpresaId(selectedValue);
                                                                setValue('id_empresa', selectedValue, { shouldDirty: true })
                                                                setValue('id_ponto_entrega', '', { shouldDirty: true })
                                                                setValue('id_estoque', '', { shouldDirty: true })
                                                                if (selectedValue) {
                                                                    clearErrors('id_empresa');
                                                                }
                                                            }}
                                                            error={!!errors.id_empresa}
                                                            helperText={getFormErrorMessage(errors, 'id_empresa')}
                                                        >
                                                            {empresas.map((option) => (
                                                                <MenuItem key={option.id} value={option.id}>
                                                                    {option.nomeFantasia}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    )}
                                                />
                                            </TooltipSelectDisabled>
                                        </Grid>

                                        {type !== 'virtual' &&
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    select
                                                    label="Tipo de Contrato"
                                                    name='tipo_contrato'
                                                    fullWidth
                                                    disabled={!dadosUsuario.isSempher}
                                                    defaultValue={equipamento ? equipamento.tipo_contrato : 'locacao'}
                                                    error={!!errors.tipo_contrato}
                                                    helperText={getFormErrorMessage(errors, 'tipo_contrato')}
                                                    {...register("tipo_contrato", { required: true })}
                                                    sx={{ mt: 1 }}
                                                >
                                                    {equipmentContractTypes.map((option) => (
                                                        <MenuItem key={option.value} value={option.value}>
                                                            {option.text}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                        }

                                        {pontosEntrega &&
                                            <Grid item xs={12} md={3}>
                                                <TooltipSelectDisabled isDisabled={selectedEmpresaId}>
                                                    <Controller
                                                        name='id_ponto_entrega'
                                                        control={control}
                                                        rules={{ required: true }}
                                                        defaultValue={equipamento ? equipamento.pontoEntrega.id : ""}
                                                        render={({ field: { ref, onChange, ...field } }) => (
                                                            <TextField
                                                                {...field}
                                                                select
                                                                fullWidth
                                                                sx={{ mt: 1 }}
                                                                label="Ponto de Entrega"
                                                                disabled={!dadosUsuario.isSempher || !selectedEmpresaId}
                                                                onChange={(e) => {
                                                                    setValue('id_ponto_entrega', e.target.value, { shouldDirty: true })
                                                                    if (e.target.value) {
                                                                        clearErrors('id_ponto_entrega');
                                                                    }
                                                                }}
                                                                error={!!errors.id_ponto_entrega}
                                                                helperText={getFormErrorMessage(errors, 'id_ponto_entrega')}
                                                            >
                                                                {pontosEntrega.length > 0 ? (
                                                                    pontosEntrega.map((option) => (
                                                                        <MenuItem key={option.id} value={option.id}>
                                                                            {option.nome}
                                                                        </MenuItem>
                                                                    ))
                                                                ) : (
                                                                    <MenuItem disabled>Nenhum disponível</MenuItem>
                                                                )}
                                                            </TextField>
                                                        )}
                                                    />
                                                </TooltipSelectDisabled>
                                            </Grid>
                                        }

                                        <Grid item xs={12} md={3}>
                                            <TooltipSelectDisabled isDisabled={selectedEmpresaId}>
                                                <Controller
                                                    name='id_estoque'
                                                    control={control}
                                                    rules={{ required: hasStockModule }}
                                                    defaultValue={equipamento ? equipamento.estoque.id : ""}
                                                    render={({ field: { ref, onChange, ...field } }) => (
                                                        <TextField
                                                            {...field}
                                                            select
                                                            fullWidth
                                                            sx={{ mt: 1 }}
                                                            label="Estoque"
                                                            disabled={!dadosUsuario.isSempher || !selectedEmpresaId || !(estoques && hasStockModule)}
                                                            onChange={(e) => {
                                                                setValue('id_estoque', e.target.value, { shouldDirty: true })
                                                                if (e.target.value) {
                                                                    clearErrors('id_estoque');
                                                                }
                                                            }}
                                                            error={!!errors.id_estoque}
                                                            helperText={getFormErrorMessage(errors, 'id_estoque')}
                                                        >
                                                            {estoques.length > 0 ? (
                                                                estoques.map((option) => (
                                                                    <MenuItem key={option.id} value={option.id}>
                                                                        {option.nome}
                                                                    </MenuItem>
                                                                ))
                                                            ) : (
                                                                <MenuItem disabled>Nenhum disponível</MenuItem>
                                                            )}
                                                        </TextField>
                                                    )}
                                                />
                                            </TooltipSelectDisabled>
                                        </Grid>
                                    </Grid>
                                }

                                {type !== 'virtual' && (
                                    <>
                                        <Grid item xs={12} md={12}>
                                            <Divider xs={12} sx={{ mt: 3 }} textAlign="center">
                                                <Typography variant="subtitle1" sx={{ color: grey[700] }}>
                                                    Dados de conexão
                                                </Typography>
                                            </Divider>
                                        </Grid>

                                        {isEditAction && (
                                            <>
                                                <Grid item xs={12} md={4}>
                                                    <TextField
                                                        autoFocus
                                                        InputLabelProps={{ shrink: true }}
                                                        margin="dense"
                                                        label="IP Ethernet"
                                                        placeholder="Ex: 192.168.0.1"
                                                        name="ip_ethernet"
                                                        fullWidth

                                                        {...register("ip_ethernet")}
                                                        error={!!errors.ip_ethernet}
                                                        helperText={getFormErrorMessage(errors, "ip_ethernet")}
                                                        disabled
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <LanIcon />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={4}>
                                                    <TextField
                                                        autoFocus
                                                        InputLabelProps={{ shrink: true }}
                                                        margin="dense"
                                                        label="IP Wifi"
                                                        placeholder="Ex: 192.168.0.1"
                                                        name="ip_wifi"
                                                        fullWidth
                                                        {...register("ip_wifi")}
                                                        error={!!errors.ip_wifi}
                                                        helperText={getFormErrorMessage(errors, "ip_wifi")}
                                                        disabled
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <RssFeedIcon />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={4}>
                                                    <TextField
                                                        autoFocus
                                                        InputLabelProps={{ shrink: true }}
                                                        margin="dense"
                                                        label="IP 4G"
                                                        placeholder="Ex: 10.0.0.1"
                                                        name="ip_4g"
                                                        fullWidth
                                                        {...register("ip_4g")}
                                                        error={!!errors.ip_4g}
                                                        helperText={getFormErrorMessage(errors, "ip_4g")}
                                                        disabled
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <FourGMobiledataIcon />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>
                                            </>
                                        )}

                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                autoFocus
                                                InputLabelProps={{ shrink: true }}
                                                margin="dense"
                                                label="MAC Ethernet"
                                                placeholder="Ex: AA:BB:CC:DD:EE:FF"
                                                name="mac_ethernet"
                                                fullWidth
                                                {...register("mac_ethernet")}
                                                error={!!errors.mac_ethernet}
                                                helperText={getFormErrorMessage(errors, "mac_ethernet")}
                                                disabled={!dadosUsuario.isSempher}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <LanIcon />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                autoFocus
                                                InputLabelProps={{ shrink: true }}
                                                margin="dense"
                                                label="MAC Wifi"
                                                placeholder="Ex: AA:BB:CC:DD:EE:FF"
                                                name="mac_wifi"
                                                fullWidth
                                                {...register("mac_wifi")}
                                                error={!!errors.mac_wifi}
                                                helperText={getFormErrorMessage(errors, "mac_wifi")}
                                                disabled={!dadosUsuario.isSempher}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <RssFeedIcon />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                autoFocus
                                                InputLabelProps={{ shrink: true }}
                                                margin="dense"
                                                label="MAC 4G"
                                                placeholder="Ex: AA:BB:CC:DD:EE:FF"
                                                name="mac_4g"
                                                fullWidth
                                                {...register("mac_4g")}
                                                error={!!errors.mac_4g}
                                                helperText={getFormErrorMessage(errors, "mac_4g")}
                                                disabled={!dadosUsuario.isSempher}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <FourGMobiledataIcon />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </FormControl>
                    </Box>
                </DialogContent>

                <CommonDialogActionsForm errors={errors} isLoading={isLoading} setOpen={setOpen} disableSubmit={!dadosUsuario.isSempher}/>
            </form>
        </Dialog>
    );
}